/**
 * Account > Companies (settings redesign, 2026-10-23): display name and branding.
 * Review cycle and approvals moved to Vault > Reviews & approvals; the TechHub
 * switch to TechHub > TechHub access; people to Account > Team.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, putFile, type AdminTenant } from "../lib/api";
import { useStore } from "../lib/store";
import { BRAND, byCoOrder, contrast, inkFor, logoColors } from "../lib/brand";
import { Loading } from "../components/bits";
import { PartTag } from "./settings/SettingsLayout";
import { CompanyPicker } from "./settings/CompanyPicker";

const SWATCHES = ["#DC6D27", "#2F6FB3", "#1F8A5B", "#7C5CC4", "#D9486B", "#1F9E8F", "#3E4651"];

export function CompaniesPage() {
  const { p, me } = useStore();
  if (!p.anyAdmin) return <Navigate to="/settings" replace />;
  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <PartTag part="account" />
          <h1 className="title">Companies</h1>
          <div className="lede">Each company's name and branding. They show everywhere: the Vault, TechHub and emails. {me.user.is_platform_admin ? "" : "You see the companies you administer."}</div>
        </div>
      </div>
      <CompanySettings />
    </div>
  );
}

// ---------------------------------------------------------------- company settings
// One company at a time: the shared company picker, the form,
// and a live preview. No colored strips or tinted headers: color shows up only
// where it is real (the preview, the swatches).
function CompanySettings() {
  const { ws } = useStore();
  const [sp, setSp] = useSearchParams();
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = async () => { try { setTenants((await api.adminTenants()).tenants.sort(byCoOrder)); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void load(); }, []);
  if (err) return <div className="note bad">{err}</div>;
  if (!tenants) return <Loading />;
  const codes = tenants.map((t) => t.code);
  const wanted = sp.get("co") ?? (ws !== "ALL" ? ws : "");
  const t = tenants.find((x) => x.code === (codes.includes(wanted) ? wanted : codes[0]));
  if (!t) return null;
  return (
    <>
      <CompanyPicker tenants={tenants} code={t.code} onPick={(c) => setSp({ co: c }, { replace: true })} sub={(x) => `${x.code} · ${x.members} member${x.members === 1 ? "" : "s"}`} />
      <CompanyPanel key={t.code + t.name + t.accent + (t.logo_url ? "l" : "")} t={t} onSaved={load} />
      <div className="foot-note">The accent colors headings, links, buttons and highlights while this company is selected. Others see changes the next time they open DocVault. "All my companies" and companies with no brand set use DocVault's own colors.</div>
    </>
  );
}

function CompanyPanel({ t, onSaved }: { t: AdminTenant; onSaved: () => Promise<void> }) {
  const { toast, me, rebrand } = useStore();
  const [name, setName] = useState(t.name);
  const [fromLogo, setFromLogo] = useState<string[]>([]);
  const [accent, setAccent] = useState(t.accent.toUpperCase());
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState<string | null>(t.logo_url);
  const file = useRef<HTMLInputElement>(null);
  const dirty = name.trim() !== t.name || accent !== t.accent.toUpperCase();
  const validHex = /^#[0-9A-F]{6}$/.test(accent);
  // Keep the brand guide's own ink (#B4561C for #DC6D27) when the accent is the built-in one.
  const ink = !validHex ? "#3E4651" : BRAND[t.code]?.accent.toUpperCase() === accent ? BRAND[t.code]!.ink : inkFor(accent);
  const onWhite = validHex ? contrast(accent, "#FFFFFF") : 0;
  const btnRatio = contrast(ink, "#FFFFFF");

  const save = async () => {
    setBusy(true);
    try {
      await api.updateCompany(t.code, {
        ...(name.trim() !== t.name ? { name: name.trim() } : {}),
        ...(accent !== t.accent.toUpperCase() ? { accent } : {}),
      });
      rebrand({ code: t.code, name: name.trim(), accent, logo_url: logo });
      toast(`Saved ${name.trim()}. The new colors apply straight away.`);
      await onSaved();
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  const upload = async (f: File) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) return toast("Logos can be PNG, JPEG or WebP.");
    if (f.size > 512 * 1024) return toast("Logos are limited to 512 KB.");
    setBusy(true);
    try {
      const { upload } = await api.logoUpload(t.code, f.type, f.size);
      await putFile(upload.url, f);
      const r = await api.setLogo(t.code, upload.key);
      setLogo(r.logo_url);
      rebrand({ code: t.code, name: t.name, accent: t.accent, logo_url: r.logo_url });
      // Pick the accent up from the new logo; the admin still presses Save.
      const cols = await logoColors(f).catch(() => [] as string[]);
      setFromLogo(cols);
      if (cols[0]) { setAccent(cols[0]); toast(`New logo saved for ${t.name}. Accent set to ${cols[0]} from the logo: press Save changes to keep it.`); }
      else toast(`New logo saved for ${t.name}.`);
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); if (file.current) file.current.value = ""; }
  };
  const removeLogo = async () => {
    try { await api.removeLogo(t.code); setLogo(null); rebrand({ code: t.code, name: t.name, accent: t.accent, logo_url: null }); toast(builtIn ? `${t.name} is back to the built-in logo.` : `Logo removed. ${t.name} has no logo now.`); await onSaved(); }
    catch (e) { toast((e as Error).message); }
  };
  const builtIn = BRAND[t.code]?.logo ?? null;
  const shownLogo = logo ?? builtIn;
  const shownAccent = validHex ? accent : t.accent;
  useEffect(() => {
    let live = true;
    if (shownLogo) logoColors(shownLogo).then((c) => { if (live) setFromLogo(c); }, () => undefined);
    else setFromLogo([]);
    return () => { live = false; };
  }, [shownLogo]);

  return (
    <div className="co-layout">
      <section className="card co-form">
        <div className="co-sec">
          <div className="co-sec-h"><h3>Company</h3><span className="cap">{t.members} member{t.members === 1 ? "" : "s"} · {t.authors} author{t.authors === 1 ? "" : "s"} · {t.approvers} approver{t.approvers === 1 ? "" : "s"} · <Link to={`/settings/people?co=${t.code}`}>Team</Link></span></div>
          <label className="lbl" htmlFor="co-name">Display name</label>
          <input id="co-name" className="inp" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          <div className="help">Shown in the switcher, on pages across DocVault and in emails. Downloaded PDFs show the document number, not the company name. The code ({t.code}) and document numbers don't change.</div>
        </div>

        <div className="co-sec">
          <div className="co-sec-h"><h3>Branding</h3></div>
          <span className="lbl">Logo</span>
          <div className="logo-row">
            <div className="logo-box">{shownLogo ? <img src={shownLogo} alt={`${t.name} logo`} /> : <span className="cap">No logo</span>}</div>
            <div>
              <div className="row-btns">
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => file.current?.click()}>{logo ? "Replace" : "Upload logo"}</button>
                {logo && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={removeLogo}>Remove</button>}
              </div>
              <div className="help">PNG, JPEG or WebP, up to 512 KB. Wide, on a transparent background.{builtIn && !logo ? " Showing the built-in logo." : ""}</div>
            </div>
            <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
          </div>
          <span className="lbl" style={{ marginTop: 16 }}>Accent color</span>
          {fromLogo.length > 0 && (
            <div className="swatches from-logo">
              <span className="cap">From your logo</span>
              {fromLogo.map((c) => <button key={c} type="button" className={`swatch ${accent === c ? "on" : ""}`} style={{ background: c }} title={c} aria-label={`Use ${c} from the logo`} onClick={() => setAccent(c)} />)}
            </div>
          )}
          <div className="swatches">
            {fromLogo.length > 0 && <span className="cap">Others</span>}
            {SWATCHES.map((c) => <button key={c} type="button" className={`swatch ${accent === c ? "on" : ""}`} style={{ background: c }} title={c} aria-label={`Use ${c}`} onClick={() => setAccent(c)} />)}
            <label className="swatch-any" title="Any color"><input type="color" value={validHex ? accent : "#3E4651"} onChange={(e) => setAccent(e.target.value.toUpperCase())} aria-label="Pick any color" /></label>
            <input className="inp hex" value={accent} maxLength={7} onChange={(e) => setAccent(e.target.value.toUpperCase())} aria-label="Hex color" />
          </div>
          {!validHex && <div className="contrast bad">Use #RRGGBB.</div>}
          {validHex && onWhite < 3 && <div className="contrast bad">This color is very light; icons on white will be hard to see.</div>}

          {me.user.is_platform_admin && t.code === "ITA" && accent !== "#DC6D27" && <div className="note warn" style={{ marginTop: 12 }}>The DocVault brand guide uses #DC6D27 for ITAssurance.</div>}
        </div>

        <div className="co-actions">
          <button className="btn btn-accent" disabled={!dirty || busy || !validHex || name.trim().length < 2} onClick={save}>Save changes</button>
          {dirty && <button className="btn btn-ghost" onClick={() => { setName(t.name); setAccent(t.accent.toUpperCase()); }}>Undo</button>}
          {dirty && <span className="cap">Unsaved changes</span>}
        </div>
      </section>

      <aside className="card co-preview" aria-label="Preview">
        <div className="eyebrow">Preview</div>
        <div className="pv-switch">
          <span className="pv-tile">{shownLogo ? <img src={shownLogo} alt="" /> : <b style={{ color: shownAccent }}>{t.code}</b>}</span>
          <span><b>{name.trim() || t.name}</b><small>Workspace</small></span>
        </div>
        <div className="pv-row"><span className="badge-no"><i style={{ background: shownAccent }} />{t.code}-POL-SEC-001 v1.0.0</span></div>
        <div className="pv-row"><span className="pv-btn" style={{ background: ink }}>New document</span><span className="pv-link" style={{ color: ink }}>Open the document</span></div>
        <dl className="pv-kv">
          <dt>Accent</dt><dd><i style={{ background: shownAccent }} />{shownAccent}</dd>
          <dt>Text &amp; buttons</dt><dd><i style={{ background: ink }} />{ink}</dd>
          <dt>Button contrast</dt><dd className={btnRatio >= 4.5 ? "ok" : "bad"}>{btnRatio.toFixed(1)}:1 {btnRatio >= 4.5 ? "· readable" : "· too low"}</dd>
        </dl>
      </aside>
    </div>
  );
}

